import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from "react-router-dom";
import Page from "../modules/page";
import NotFound from "../modules/not-found";
import RouteErrorPage from "../modules/route-error";

import { setData, setSessionData } from "../redux/actions";

import ProtectedRoute from "./ProtectedRoute";
import PermissionRoute from "./PermissionRoute";
import { Admins } from "./admin";
import { Auth } from "./auth";
import { Users } from "./user";
import { Spin } from "antd";
import { DefaultAdminPage } from "../config";
import SignIn from "../modules/auth/sign-in";
import { isAuthenticated, normalizeLoginType } from "../utils/auth";
import { ROUTE_PERMISSIONS } from "../constants/menuPermissions";
import { getDefaultUserRoute } from "../utils/userRoutes";

const pages = {
  admin: Admins,
  user: Users,
};

const getDefaultPage = (loginType, userData) => {
  const normalizedType = normalizeLoginType(loginType);
  if (normalizedType === "admin") return DefaultAdminPage;
  if (normalizedType === "user") return getDefaultUserRoute(userData);
  return "/sign-in";
};

function AuthenticatedLayout() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.data);
  const session = useSelector((state) => state.session.data);

  return (
    <Page
      user={user}
      setData={(payload) => dispatch(setData(payload))}
      setSessionData={(payload) => dispatch(setSessionData(payload))}
      session={session}
    />
  );
}

export default function Routers({ loading }) {
  const user = useSelector((state) => state.user.data);
  const loginType = normalizeLoginType(user?.loginType);
  const authenticated = isAuthenticated(user);
  const defaultPage = getDefaultPage(loginType, user);

  const router = useMemo(() => {
    const routes = [
      {
        path: "salla-webhook",
        element: <SignIn />,
      },
      {
        path: "zid-webhook",
        element: <SignIn />,
      },
      ...Auth,
    ];

    if (authenticated) {
      const childRoutes = (pages[loginType] || []).map((d) => {
        const routePermissions = ROUTE_PERMISSIONS[d.path] || [];
        const RouteWrapper =
          loginType === "user" && routePermissions.length
            ? PermissionRoute
            : ProtectedRoute;

        return {
          path: d.path,
          element: (
            <RouteWrapper
              permissions={routePermissions}
              routePath={d.path}
              element={d.element}
            />
          ),
        };
      });

      routes.push({
        path: "/",
        element: <AuthenticatedLayout />,
        children: [
          {
            index: true,
            element: <Navigate to={defaultPage} replace />,
          },
          ...childRoutes,
          {
            path: "*",
            element: <NotFound />,
          },
        ],
      });
    } else {
      routes.push({
        path: "/",
        element: <Navigate to="/sign-in" replace />,
      });
      routes.push({
        path: "*",
        element: <Navigate to="/sign-in" replace />,
      });
    }

    return createBrowserRouter([
      {
        errorElement: <RouteErrorPage />,
        children: routes,
      },
    ]);
  }, [authenticated, loginType, defaultPage, user]);

  if (loading) {
    return <Spin />;
  }

  return <RouterProvider router={router} />;
}
